import { warn } from '@execonline-inc/logging';
import { assertNever, noop } from '@kofno/piper';
import { Task } from 'taskarian';
import LearningCollectionEditStore from '..';
import { callApi } from '../../../../../../Appy';
import { Error } from '../../../../../../ErrorHandling';
import { pushState } from '../../../../../../History';
import { findLink, findLinkT } from '../../../../../../LinkyLinky';
import ReactionComponent from '../../../../../../ReactionComponent';
import { FormTemplate } from '../../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../../Resource/Types';
import { fromNullableT } from '../../../../../../TaskExt';
import { learningCollectionEditFormResourceDecoder } from '../../../Decoders';
import learningCollectionEncoder from '../../../Reactions/LearningCollectionEncoder';
import { LearningCollectionError, Templates } from '../../../Types';
import {
  Finishing,
  LearningCollectionEditFormResource,
  LearningCollectionEditableStep,
  LearningCollectionState,
  PreviewingUpdating,
  SavingDetails,
  Updating,
} from '../Types';
import { addLearningDesigns } from './AddingLearningDesign';
import { deleteSection } from './DeleteSection';
import loadForDeepLinks from './LoadForDeepLinks';
import loadForEdits from './LoadForEdits';
import { loadForInvitations } from './LoadForInvitations';
import previewUpdating from './PreviewUpdating';
import ready from './Ready';
import saveDetails from './SaveDetails';
import update from './Update';
import { updateProductLicense } from './UpdateProductLicense';
import { Maybe } from 'maybeasy';
import { FileMetaData } from '../../CurrentCollectionStep/Details/S3ImageUpload/Types';

export interface Props {
  store: LearningCollectionEditStore;
}

export const readyPublishStore = (
  store: LearningCollectionEditStore,
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  template: FormTemplate,
) =>
  store.publishingStore.ready(
    findLink('publish', learningCollectionEditFormResource.links),
    learningCollectionEditFormResource.payload.publishing,
    template,
  );

export const editTemplatesCheck = (
  learningCollectionFormResource: LearningCollectionEditFormResource,
): Task<LearningCollectionError, Templates> =>
  Task.succeed<Error, {}>({})
    .assign('template', fromNullableT(learningCollectionFormResource.templates.self))
    .assign(
      'publishTemplate',
      fromNullableT(learningCollectionFormResource.payload.publishing.templates.self),
    );

export const updateLearningCollection = (
  store: LearningCollectionEditStore,
  state: Updating | PreviewingUpdating | Finishing | SavingDetails,
  fileMetaData: Maybe<FileMetaData>,
) =>
  Task.succeed<LearningCollectionError, ReadonlyArray<Link>>(store.links)
    .andThen(findLinkT('update'))
    .andThen(
      callApi(
        learningCollectionEditFormResourceDecoder,
        learningCollectionEncoder(
          store,
          state,
          fileMetaData,
          state.learningCollectionEditFormResource.payload.warningStatus,
        ),
      ),
    );

const editPathForStep = (
  resource: LearningCollectionEditFormResource,
  step: LearningCollectionEditableStep,
): string => `/learning_collections/${resource.payload.id}/edit/${step}`;

const updateUrlForStep = (
  resource: LearningCollectionEditFormResource,
  step: LearningCollectionEditableStep,
) =>
  pushState({}, '', editPathForStep(resource, step)).fork(
    () => console.warn(`LearningCollectionEditReactions -- unable to update ${step} URL`),
    noop,
  );

class LearningCollectionEditReactions extends ReactionComponent<
  LearningCollectionEditStore,
  LearningCollectionState,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: LearningCollectionState) => {
    const { store } = this.props;

    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        loadForEdits(state, store);
        break;
      case 'ready':
        ready(store, state);
        break;
      case 'editing-details':
        updateUrlForStep(state.learningCollectionEditFormResource, 'describe');
        break;
      case 'building':
        updateUrlForStep(state.learningCollectionEditFormResource, 'customize');
        break;
      case 'previewing':
        updateUrlForStep(state.learningCollectionEditFormResource, 'preview');
        break;
      case 'choosing-learning-designs':
        break;
      case 'sharing':
        updateUrlForStep(state.learningCollectionEditFormResource, 'share');
        break;
      case 'adding-learning-design':
        addLearningDesigns(store, state);
        break;
      case 'deleting-section':
        deleteSection(store, state);
        break;
      case 'saving-details':
        saveDetails(store, state, state.fileMetaData);
        break;
      case 'finishing':
      case 'finished':
        break;
      case 'updating':
        update(store, state);
        break;
      case 'previewing-updating':
        previewUpdating(store, state);
        break;
      case 'load-error':
        warn('Could not load the learning collection:', JSON.stringify(state.error));
        break;
      case 'edit-form-submission-error':
        warn('Could not save the learning collection:', JSON.stringify(state.error));
        break;
      case 'loading-for-deep-links':
        loadForDeepLinks(state, store);
        break;
      case 'loading-for-invitations':
        loadForInvitations(state, store);
        break;
      case 'ready-invitations':
      case 'ready-deep-links':
        break;
      case 'updating-product-license':
        updateProductLicense(state, store);
        break;
      default:
        assertNever(state);
    }
  };
}

export default LearningCollectionEditReactions;
